import React, { useState, useMemo, useCallback } from 'react';
import { Container, Row, Col, Card, Button, Badge, Form } from 'react-bootstrap';
import { motion } from 'framer-motion';
import { 
  FaShieldAlt,
  FaServer, 
  FaCheckCircle,
  FaArrowRight,
  FaPhone,
  FaExternalLinkAlt,
  FaMicrochip,
  FaMemory,
  FaHdd,
  FaRocket,
  FaCog,
  FaNetworkWired,
  FaDatabase,
  FaCloud,
  FaFilter,
  FaSearch,
  FaTh,
  FaList,
  FaChevronLeft,
  FaChevronRight,
  FaLock,
  FaFire,
  FaEye,
  FaBuilding,
  FaIndustry,
  FaCube,
  FaGlobe
} from 'react-icons/fa';
import { useScrollAnimation } from '../../../hooks';
import { useLanguage } from '../../../contexts/LanguageContext'; // Assurez-vous que le chemin est correct
import { Helmet } from 'react-helmet-async';

// Animation variants
const animationVariants = {
  fadeInUp: {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.8 }
  },
  scaleIn: {
    initial: { opacity: 0, scale: 0.8 },
    animate: { opacity: 1, scale: 1 },
    transition: { duration: 0.6 }
  }
};

const Firewall = () => {
  const { t } = useLanguage();
  
  // Animation hooks
  const [setHeroRef, heroInView] = useScrollAnimation(0.1);
  const [setCategoriesRef, categoriesInView] = useScrollAnimation(0.1);
  const [setSolutionsRef, solutionsInView] = useScrollAnimation(0.1);

  // État des filtres et recherche
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('grid'); // grid ou list
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 4;

  // Données structurées utilisant les traductions du contexte
  const FIREWALL_DATA = useMemo(() => ({
    categories: [
      { id: 'data-center', name: t.firewall.categories['data-center'].name, description: t.firewall.categories['data-center'].desc, icon: <FaBuilding /> },
      { id: 'campus-enterprise', name: t.firewall.categories['campus-enterprise'].name, description: t.firewall.categories['campus-enterprise'].desc, icon: <FaIndustry /> },
      { id: 'branch-office', name: t.firewall.categories['branch-office'].name, description: t.firewall.categories['branch-office'].desc, icon: <FaNetworkWired /> },
      { id: 'cloud-security', name: t.firewall.categories['cloud-security'].name, description: t.firewall.categories['cloud-security'].desc, icon: <FaCloud /> }
    ],
    solutions: [
      { id: 'f7121f', ...t.firewall.products.f7121f, category: 'data-center', image: '/images/Firewall/fortigate7121f.png' },
      { id: 'f6500f', ...t.firewall.products.f6500f, category: 'data-center', image: '/images/Firewall/fortigate6500f.png' },
      { id: 'f4800f', ...t.firewall.products.f4800f, category: 'data-center', image: '/images/Firewall/fortigate4800f.png' },
      { id: 'f1800f', ...t.firewall.products.f1800f, category: 'campus-enterprise', image: '/images/Firewall/fortigate1800f.png' },
      { id: 'f1000f', ...t.firewall.products.f1000f, category: 'campus-enterprise', image: '/images/Firewall/fortigate1000f.png', imageSize: { width: '80%', height: '90%', objectFit: 'contain' }, imagePosition: { objectPosition: 'right top' } },
      { id: 'f600f', ...t.firewall.products.f600f, category: 'branch-office', image: '/images/Firewall/fortigate600f.png' },
      { id: 'f200f', ...t.firewall.products.f200f, category: 'branch-office', image: '/images/Firewall/fortigate200f.png' },
      { id: 'fvm', ...t.firewall.products.fvm, category: 'cloud-security', image: '/images/Firewall/fgvm.png' }
    ]
  }), [t]);

  // Filtrage des solutions
  const filteredSolutions = useMemo(() => {
    return FIREWALL_DATA.solutions.filter(solution => {
      const matchCategory = selectedCategory === 'all' || solution.category === selectedCategory;
      const matchSearch = solution.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         solution.features.some(feature => feature.toLowerCase().includes(searchTerm.toLowerCase())) ||
                         solution.workloads.some(workload => workload.toLowerCase().includes(searchTerm.toLowerCase()));
      return matchCategory && matchSearch;
    });
  }, [selectedCategory, searchTerm, FIREWALL_DATA]);

  // Pagination
  const paginatedSolutions = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredSolutions.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredSolutions, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredSolutions.length / itemsPerPage);

  // Handlers
  const handleCategoryChange = useCallback((category) => {
    setSelectedCategory(category);
    setCurrentPage(1);
  }, []);

  const handleSearchChange = useCallback((e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  }, []);

  const scrollToSolutions = () => {
    document.getElementById('solutions-section').scrollIntoView({ 
      behavior: 'smooth' 
    });
  };

  return (
    <div>

       {/* --- SEO DYNAMIQUE POUR GOOGLE --- */}
      <Helmet>
        <title>{t.firewall.seo.title}</title>
        <meta name="description" content={t.firewall.seo.description} />
        <link rel="canonical" href="https://www.s2i.com.tn/solutions/firewall" />
        
        {/* Balises Open Graph (LinkedIn / Facebook) */}
        <meta property="og:title" content={t.firewall.seo.title} />
        <meta property="og:description" content={t.firewall.seo.description} />
        <meta property="og:image" content="https://www.s2i.com.tn/images/Firewall/fortigate1800f.png" />
        <meta property="og:type" content="product" />
      </Helmet>
      {/* ---------------------------------- */}

      {/* Section Hero */}
      <section 
        ref={setHeroRef}
        className="hero-section-sii" 
        style={{ 
          background: `linear-gradient(135deg, rgba(30, 41, 59, 0.9) 0%, rgba(71, 85, 105, 0.9) 100%), url('/images/cybersecurity-background.webp')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed',
          minHeight: '80vh',
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <div 
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.05"%3E%3Ccircle cx="30" cy="30" r="4"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
            opacity: 0.3
          }}
        />

        <Container style={{ position: 'relative', zIndex: 1 }}>
          <Row className="align-items-center">
            <Col lg={6}>
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                animate={heroInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.8 }}
              >
                <motion.div
                  {...animationVariants.scaleIn}
                  transition={{ delay: 0.2 }}
                  className="mb-4"
                >
                  <span className="badge-sii">
                    <FaFire className="me-2" />
                    {t.firewall.hero.badge}
                  </span>
                </motion.div>

                <motion.h1 
                  className="display-3 fw-bold mb-4 text-white"
                  initial={{ opacity: 0, x: -50 }}
                  animate={heroInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ duration: 0.8, delay: 0.3 }}
                  style={{
                    fontSize: 'clamp(2rem, 5vw, 3.5rem)',
                    lineHeight: '1.1',
                    fontWeight: '700'
                  }}
                  dangerouslySetInnerHTML={{ __html: t.firewall.hero.title }}
                />

                <motion.p 
                  className="lead text-light mb-4"
                  initial={{ opacity: 0, y: 20 }}
                  animate={heroInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.8, delay: 0.5 }}
                  style={{
                    fontSize: 'clamp(1rem, 2.5vw, 1.25rem)',
                    maxWidth: '600px',
                    lineHeight: '1.6',
                    color: 'rgba(255,255,255,0.9)'
                  }}
                >
                  {t.firewall.hero.subtitle}
                </motion.p>

                <motion.div
                  className="d-flex flex-wrap gap-3 mb-5"
                  initial={{ opacity: 0, y: 30 }}
                  animate={heroInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.8, delay: 0.7 }}
                >
                  <Button 
                    size="lg" 
                    onClick={scrollToSolutions}
                    style={{
                      borderRadius: '16px',
                      padding: '1rem 2.5rem',
                      fontWeight: 600,
                      fontSize: '1rem',
                      background: '#d90000',
                      color: 'white',
                      border: 'none',
                      boxShadow: '0 4px 16px rgba(217, 0, 0, 0.3)',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    <FaRocket className="me-2" />
                    {t.firewall.hero.btnExplore}
                  </Button>
                  <Button 
                    variant="outline-light" 
                    size="lg"
                    href="tel:+21671000000"
                    style={{
                      borderRadius: '16px',
                      padding: '1rem 2.5rem',
                      fontWeight: 600,
                      fontSize: '1rem',
                      borderWidth: '2px',
                      background: 'rgba(255,255,255,0.1)',
                      backdropFilter: 'blur(10px)',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    <FaPhone className="me-2" />
                    {t.firewall.hero.btnContact}
                  </Button>
                </motion.div>
              </motion.div>
            </Col>

            <Col lg={6}>
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                animate={heroInView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="text-center"
              >
                <div 
                  style={{
                    background: 'rgba(255, 255, 255, 0.1)',
                    borderRadius: '24px',
                    padding: '3rem',
                    backdropFilter: 'blur(20px)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
                  }}
                >
                  <h3 className="text-white mb-4 fw-bold">
                    {t.firewall.hero.whyTitle}
                  </h3>
                  <div className="text-start">
                    {t.firewall.hero.whyList.map((feature, index) => (
                      <motion.div
                        key={index}
                        className="d-flex align-items-center mb-3"
                        initial={{ opacity: 0, x: -20 }}
                        animate={heroInView ? { opacity: 1, x: 0 } : {}}
                        transition={{ duration: 0.5, delay: 0.9 + (index * 0.1) }}
                      >
                        <FaCheckCircle 
                          className="me-3 text-success" 
                          style={{ minWidth: '20px' }}
                        />
                        <span className="text-white">{feature}</span>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </motion.div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Section Catégories */}
      <section className="categories-section py-5 bg-light">
        <Container fluid>
          <motion.div
            ref={setCategoriesRef}
            variants={animationVariants.fadeInUp}
            initial="initial"
            animate={categoriesInView ? "animate" : "initial"}
            className="text-center mb-5"
          >
            <h2 className="fw-bold mb-3" dangerouslySetInnerHTML={{ __html: t.firewall.categories.title }} />
            <p className="text-muted lead">
              {t.firewall.categories.subtitle}
            </p>
          </motion.div>

          <Row className="g-4 justify-content-center">
            {FIREWALL_DATA.categories.map((category, index) => (
              <Col key={category.id} lg={3} md={6}>
                <motion.div
                  variants={animationVariants.scaleIn}
                  initial="initial"
                  animate={categoriesInView ? "animate" : "initial"}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card 
                    className="h-100 text-center border-0 shadow-sm category-card"
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleCategoryChange(category.id)}
                  >
                    <Card.Body className="p-4">
                      <div className="mb-3" style={{ fontSize: '3rem', color: '#d90000' }}>
                        {category.icon}
                      </div>
                      <h5 className="fw-bold mb-2">{category.name}</h5>
                      <p className="text-muted small">{category.description}</p>
                    </Card.Body>
                  </Card>
                </motion.div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Section Solutions */}
      <section id="solutions-section" className="solutions-section py-5">
        <Container fluid>
          <motion.div
            ref={setSolutionsRef}
            variants={animationVariants.fadeInUp}
            initial="initial"
            animate={solutionsInView ? "animate" : "initial"}
            className="mb-5"
          >
            <Row>
              {/* Sidebar Filtres */}
              <Col lg={3}>
                <div style={{
                  position: 'sticky',
                  top: '100px',
                  background: '#fff',
                  borderRadius: '20px',
                  padding: '2rem',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                  border: '1px solid #e9ecef',
                  height: 'fit-content'
                }}>
                  {/* Titre de la Sidebar */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: '2rem',
                    paddingBottom: '1rem',
                    borderBottom: '2px solid #e9ecef'
                  }}>
                    <FaFilter style={{ 
                      fontSize: '1.2rem', 
                      color: '#d90000',
                      marginRight: '0.75rem'
                    }} />
                    <h5 style={{
                      margin: 0,
                      fontWeight: 700,
                      color: '#1a202c',
                      fontSize: '1.1rem'
                    }}>
                      {t.firewall.sidebar.title}
                    </h5>
                  </div>

                  {/* Barre de recherche */}
                  <div className="mb-4">
                    <label style={{
                      fontSize: '0.85rem',
                      fontWeight: 600,
                      color: '#4a5568',
                      marginBottom: '0.75rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      display: 'block'
                    }}>
                      {t.firewall.sidebar.search}
                    </label>
                    <div className="position-relative">
                      <FaSearch 
                        style={{
                          position: 'absolute',
                          left: '1rem',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          color: '#999',
                          fontSize: '0.9rem'
                        }}
                      />
                      <Form.Control
                        type="text"
                        placeholder={t.firewall.sidebar.searchPl}
                        value={searchTerm}
                        onChange={handleSearchChange}
                        style={{
                          borderRadius: '12px',
                          border: '2px solid #e0e0e0',
                          paddingLeft: '2.5rem',
                          paddingRight: '1rem',
                          paddingTop: '0.75rem',
                          paddingBottom: '0.75rem',
                          fontSize: '0.9rem',
                          background: '#f8f9fa'
                        }}
                      />
                    </div>
                  </div>

                  {/* Filtre par catégorie */}
                  <div className="mb-4">
                    <label style={{
                      fontSize: '0.85rem',
                      fontWeight: 600,
                      color: '#4a5568',
                      marginBottom: '0.75rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      display: 'block'
                    }}>
                      {t.firewall.sidebar.cat}
                    </label>
                    
                    <div className="d-grid gap-2">
                      <Button
                        variant={selectedCategory === 'all' ? 'danger' : 'outline-secondary'}
                        onClick={() => handleCategoryChange('all')}
                        style={{
                          borderRadius: '12px',
                          padding: '0.75rem 1rem',
                          fontWeight: 500,
                          fontSize: '0.9rem',
                          textAlign: 'left',
                          border: selectedCategory === 'all' ? '2px solid #d90000' : '1px solid #dee2e6'
                        }}
                      >
                        <FaFire className="me-2" style={{ fontSize: '0.8rem' }} />
                        {t.firewall.sidebar.all} ({FIREWALL_DATA.solutions.length})
                      </Button>

                      {FIREWALL_DATA.categories.map(category => (
                        <Button
                          key={category.id}
                          variant={selectedCategory === category.id ? 'danger' : 'outline-secondary'}
                          onClick={() => handleCategoryChange(category.id)}
                          style={{
                            borderRadius: '12px',
                            padding: '0.75rem 1rem',
                            fontWeight: 500,
                            fontSize: '0.85rem',
                            textAlign: 'left',
                            border: selectedCategory === category.id ? '2px solid #d90000' : '1px solid #dee2e6'
                          }}
                        >
                          <span className="me-2" style={{ fontSize: '0.8rem' }}>
                            {category.icon}
                          </span>
                          {category.name}
                        </Button>
                      ))}
                    </div>
                  </div>

                  {/* Résultats et options d'affichage */}
                  <div style={{
                    padding: '1rem',
                    background: 'linear-gradient(135deg, #f8f9fa, #e9ecef)',
                    borderRadius: '12px',
                    border: '1px solid #dee2e6',
                    marginBottom: '1.5rem'
                  }}>
                    <p style={{ 
                      color: '#495057', 
                      margin: 0,
                      fontWeight: 600,
                      fontSize: '0.85rem',
                      textAlign: 'center'
                    }}>
                      <strong style={{ color: '#d90000' }}>{filteredSolutions.length}</strong> {t.firewall.sidebar.results}
                    </p>
                    {totalPages > 1 && (
                      <p style={{ 
                        color: '#6c757d', 
                        margin: '0.25rem 0 0 0',
                        fontSize: '0.75rem',
                        textAlign: 'center'
                      }}>
                        Page {currentPage} {t.firewall.sidebar.pageOf} {totalPages}
                      </p>
                    )}
                  </div>

                  {/* Options d'affichage */}
                  <div className="mb-3">
                    <label style={{
                      fontSize: '0.85rem',
                      fontWeight: 600,
                      color: '#4a5568',
                      marginBottom: '0.75rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      display: 'block'
                    }}>
                      {t.firewall.sidebar.view}
                    </label>
                    
                    <div className="btn-group d-grid" style={{ borderRadius: '12px', overflow: 'hidden' }}>
                      <Button
                        variant={viewMode === 'grid' ? 'danger' : 'outline-secondary'}
                        onClick={() => setViewMode('grid')}
                        style={{ 
                          border: 'none', 
                          fontSize: '0.9rem',
                          borderRadius: '12px 12px 0 0'
                        }}
                      >
                        <FaTh className="me-2" />
                        {t.firewall.sidebar.grid}
                      </Button>
                      <Button
                        variant={viewMode === 'list' ? 'danger' : 'outline-secondary'}
                        onClick={() => setViewMode('list')}
                        style={{ 
                          border: 'none', 
                          fontSize: '0.9rem',
                          borderRadius: '0 0 12px 12px'
                        }}
                      >
                        <FaList className="me-2" />
                        {t.firewall.sidebar.list}
                      </Button>
                    </div>
                  </div>
                </div>
              </Col>

              {/* Contenu principal */}
              <Col lg={9}>
                {/* En-tête de section */}
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <div>
                    <h3 className="fw-bold mb-1" dangerouslySetInnerHTML={{ __html: t.firewall.sidebar.resultsTitle || `Pare-feux <span style="color: #d90000">FortiGate</span>` }} />
                    <p className="text-muted mb-0">
                      {filteredSolutions.length} {t.firewall.available || "solution(s) disponible(s)"}
                    </p>
                  </div>
                  {totalPages > 1 && (
                    <div className="pagination-info">
                      <small className="text-muted">
                        Page {currentPage} {t.firewall.sidebar.pageOf} {totalPages}
                      </small>
                    </div>
                  )}
                </div>

                {/* Solutions Grid/List */}
                {viewMode === 'grid' ? (
                  <Row className="g-4 mb-4">
                    {paginatedSolutions.map((solution, index) => (
                      <Col key={solution.id} lg={6} md={6}>
                        <motion.div
                          variants={animationVariants.scaleIn}
                          initial="initial"
                          animate={solutionsInView ? "animate" : "initial"}
                          transition={{ delay: index * 0.1 }}
                          style={{ height: '100%' }}
                        >
                          <Card className="h-100 shadow-sm border-0 solution-card">
                            <div 
                              className="position-relative overflow-hidden"
                              style={{ 
                                height: '220px',
                                background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)'
                              }}
                            >
                              <div className="position-absolute top-0 end-0 p-3 d-flex flex-column gap-2" style={{ zIndex: 2 }}>
                                {solution.isNew && (
                                  <Badge bg="success" className="px-2 py-1">
                                    <small>{t.firewall.card.new}</small>
                                  </Badge>
                                )}
                                {solution.badge && (
                                  <Badge bg="danger" className="px-2 py-1">
                                    <small>{solution.badge}</small>
                                  </Badge>
                                )}
                              </div>

                              <img 
                                src={solution.image} 
                                alt={solution.name}
                                style={{
                                  width: solution.imageSize?.width || '100%',
                                  height: solution.imageSize?.height || '100%',
                                  objectFit: solution.imageSize?.objectFit || 'contain',
                                  objectPosition: solution.imagePosition?.objectPosition || 'center center',
                                  position: 'relative',
                                  zIndex: 1
                                }}
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'flex';
                                }}
                              />
                              
                              <div style={{
                                display: 'none',
                                alignItems: 'center',
                                justifyContent: 'center',
                                position: 'relative',
                                zIndex: 1,
                                width: '100%',
                                height: '100%'
                              }}>
                                <FaFire style={{ fontSize: '4rem', color: '#d90000' }} />
                              </div>
                            </div>

                            <Card.Body className="d-flex flex-column">
                              <div className="mb-3">
                                <h5 className="fw-bold mb-2">{solution.name}</h5>
                                <div className="d-flex flex-wrap gap-1 mb-2">
                                  {solution.workloads.slice(0, 3).map((workload, idx) => (
                                    <Badge key={idx} bg="light" text="dark" className="small">
                                      {workload}
                                    </Badge>
                                  ))}
                                </div>
                              </div>

                              <div className="specs-grid mb-3">
                                <div className="row g-2">
                                  <div className="col-6">
                                    <div className="d-flex align-items-center gap-2">
                                      <FaMicrochip className="text-danger" />
                                      <div>
                                        <small className="text-muted">{t.firewall.card.processor}</small>
                                        <div className="small fw-semibold">{solution.specs.processor}</div>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="col-6">
                                    <div className="d-flex align-items-center gap-2">
                                      <FaMemory className="text-danger" />
                                      <div>
                                        <small className="text-muted">{t.firewall.card.performance}</small>
                                        <div className="small fw-semibold">{solution.specs.memory}</div>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="col-6">
                                    <div className="d-flex align-items-center gap-2">
                                      <FaHdd className="text-danger" />
                                      <div>
                                        <small className="text-muted">{t.firewall.card.interfaces}</small>
                                        <div className="small fw-semibold">{solution.specs.storage}</div>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="col-6">
                                    <div className="d-flex align-items-center gap-2">
                                      <FaNetworkWired className="text-danger" />
                                      <div>
                                        <small className="text-muted">{t.firewall.card.network}</small>
                                        <div className="small fw-semibold">{solution.specs.network}</div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>

                              <div className="mb-3 flex-grow-1">
                                <small className="text-muted text-uppercase fw-semibold">{t.firewall.card.featuresTitle}</small>
                                <ul className="list-unstyled mt-2 mb-0">
                                  {solution.features.slice(0, 4).map((feature, idx) => (
                                    <li key={idx} className="d-flex align-items-start gap-2 mb-1">
                                      <FaCheckCircle className="text-success mt-1" size={12} />
                                      <small>{feature}</small>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            </Card.Body>
                          </Card>
                        </motion.div>
                      </Col>
                    ))}
                  </Row>
                ) : (
                  /* Vue Liste */
                  <div className="mb-4">
                    {paginatedSolutions.map((solution, index) => (
                      <motion.div
                        key={solution.id}
                        variants={animationVariants.fadeInUp}
                        initial="initial"
                        animate={solutionsInView ? "animate" : "initial"}
                        transition={{ delay: index * 0.1 }}
                        className="mb-3"
                      >
                        <Card className="border-0 shadow-sm">
                          <Card.Body>
                            <Row className="align-items-center">
                              <Col lg={2}>
                                <div style={{
                                  position: 'relative',
                                  borderRadius: '12px',
                                  overflow: 'hidden',
                                  backgroundColor: '#f8f9fa',
                                  padding: '0.5rem',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  width: '140px',
                                  height: '140px'
                                }}>
                                  <img 
                                    src={solution.image} 
                                    alt={solution.name}
                                    style={{
                                      width: solution.imageSize?.width || '100%',
                                      height: solution.imageSize?.height || '100%',
                                      objectFit: solution.imageSize?.objectFit || 'contain',
                                      objectPosition: solution.imagePosition?.objectPosition || 'center center',
                                      borderRadius: '12px'
                                    }}
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                      e.target.nextSibling.style.display = 'block';
                                    }}
                                  />
                                  <FaFire style={{ 
                                    fontSize: '3rem', 
                                    color: '#d90000',
                                    display: 'none'
                                  }} />
                                </div>
                                {solution.isNew && (
                                  <Badge 
                                    bg="success"
                                    className="position-absolute top-0 end-0 translate-middle px-2 py-1"
                                    style={{ zIndex: 2 }}
                                  >
                                    <small>{t.firewall.card.new}</small>
                                  </Badge>
                                )}
                              </Col>

                              <Col lg={10}>
                                <Row>
                                  <Col lg={8}>
                                    <div className="d-flex align-items-center gap-2 mb-2">
                                      <h5 className="fw-bold mb-0">{solution.name}</h5>
                                      {solution.badge && (
                                        <Badge bg="danger" className="px-2 py-1">
                                          <small>{solution.badge}</small>
                                        </Badge>
                                      )}
                                    </div>
                                    
                                    <div className="d-flex flex-wrap gap-1 mb-3">
                                      {solution.workloads.map((workload, idx) => (
                                        <Badge key={idx} bg="light" text="dark" className="small">
                                          {workload}
                                        </Badge>
                                      ))}
                                    </div>

                                    <div className="mb-2">
                                      <small className="text-muted text-uppercase fw-semibold">{t.firewall.card.featuresTitle}</small>
                                      <ul className="list-unstyled mt-1 mb-0">
                                        {solution.features.slice(0, 3).map((feature, idx) => (
                                          <li key={idx} className="d-flex align-items-start gap-2 mb-1">
                                            <FaCheckCircle className="text-success mt-1" size={12} />
                                            <small>{feature}</small>
                                          </li>
                                        ))}
                                      </ul>
                                    </div>
                                  </Col>

                                  <Col lg={4}>
                                    <div className="specs-summary">
                                      <small className="text-muted text-uppercase fw-semibold mb-2 d-block">
                                        Spécifications
                                      </small>
                                      <div className="d-flex flex-column gap-1">
                                        <div className="d-flex align-items-center gap-2">
                                          <FaMicrochip className="text-danger" size={14} />
                                          <small className="text-truncate">{solution.specs.processor}</small>
                                        </div>
                                        <div className="d-flex align-items-center gap-2">
                                          <FaMemory className="text-danger" size={14} />
                                          <small className="text-truncate">{solution.specs.memory}</small>
                                        </div>
                                        <div className="d-flex align-items-center gap-2">
                                          <FaHdd className="text-danger" size={14} />
                                          <small className="text-truncate">{solution.specs.storage}</small>
                                        </div>
                                        <div className="d-flex align-items-center gap-2">
                                          <FaNetworkWired className="text-danger" size={14} />
                                          <small className="text-truncate">{solution.specs.network}</small>
                                        </div>
                                      </div>
                                    </div>
                                  </Col>
                                </Row>
                              </Col>
                            </Row>
                          </Card.Body>
                        </Card>
                      </motion.div>
                    ))}
                  </div>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="d-flex justify-content-center align-items-center gap-3 mt-4">
                    <Button
                      variant="outline-danger"
                      size="sm"
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage(currentPage - 1)}
                    >
                      <FaChevronLeft />
                    </Button>

                    <div className="d-flex gap-1">
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                        <Button
                          key={page}
                          variant={currentPage === page ? "danger" : "outline-secondary"}
                          size="sm"
                          onClick={() => setCurrentPage(page)}
                        >
                          {page}
                        </Button>
                      ))}
                    </div>

                    <Button
                      variant="outline-danger"
                      size="sm"
                      disabled={currentPage === totalPages}
                      onClick={() => setCurrentPage(currentPage + 1)}
                    >
                      <FaChevronRight />
                    </Button>
                  </div>
                )}
              </Col>
            </Row>
          </motion.div>
        </Container>
      </section>

      {/* CTA Section */}
      <section 
        className="cta-section-sii" 
        style={{ 
          padding: '6rem 0',
          background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)'
        }}
      >
        <Container>
          <Row>
            <Col lg={12} className="text-center">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                style={{
                  background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
                  borderRadius: '24px',
                  padding: '4rem 3rem',
                  color: 'white'
                }}
              >
                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  marginBottom: '2rem',
                  gap: '1rem'
                }}>
                  <div style={{ width: '60px', height: '60px', borderRadius: '50%', background: 'rgba(217,0,0,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <FaFire size={24} color="#d90000" />
                  </div>
                  <div style={{ width: '60px', height: '60px', borderRadius: '50%', background: 'rgba(217,0,0,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <FaShieldAlt size={24} color="#d90000" />
                  </div>
                  <div style={{ width: '60px', height: '60px', borderRadius: '50%', background: 'rgba(217,0,0,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <FaGlobe size={24} color="#d90000" />
                  </div>
                </div>
                
                <h3 style={{ fontWeight: 700, marginBottom: '1.5rem', fontSize: '2.5rem' }}>
                  {t.firewall.cta.title}
                </h3>
                <p style={{ fontSize: '1.2rem', marginBottom: '2.5rem', opacity: 0.9, maxWidth: '800px', margin: '0 auto 2.5rem auto' }}>
                  {t.firewall.cta.subtitle}
                </p>
                
                <div className="d-flex justify-content-center gap-3 flex-wrap">
                  <Button variant="light" size="lg" style={{ borderRadius: '16px', padding: '1rem 2rem', fontWeight: 600 }}
                  onClick={() => {
                    window.location.href = '/#contact';
                    setTimeout(() => {
                      document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }, 100);
                  }}
                  >
                    <FaPhone className="me-2" />
                    {t.firewall.cta.btn1}
                  </Button>
                  <Button variant="outline-light" size="lg" style={{ borderRadius: '16px', padding: '1rem 2rem', fontWeight: 600, borderWidth: '2px' }}>
                    <FaExternalLinkAlt className="me-2" />
                    {t.firewall.cta.btn2}
                  </Button>
                </div>
              </motion.div>
            </Col>
          </Row>
        </Container>
      </section>
    </div>
  );
};

export default Firewall;